// Message Variables System
// This file contains all message variable definitions, types, and utilities

export interface MessageVariable {
  id: string;
  name: string;
  variable: string; // e.g., ":contact_first_name:"
  description: string;
  category: 'contact' | 'business' | 'links' | 'custom';
  isGlobal: boolean; // true for system-wide variables, false for user-customized
  isActive: boolean;
  dataSource: 'contact' | 'business' | 'system' | 'api';
  dataField?: string; // specific field to pull data from
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string; // user ID who created custom variable
}

export interface MessageVariableContext {
  contact?: {
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
    phone_number: string;
    email?: string;
  };
  business?: {
    id: string;
    name: string;
    contact_first_name: string;
    contact_last_name: string;
    contact_full_name: string;
    office_number: string;
    email?: string;
  };
  system?: {
    current_user_id: string;
    current_business_id: string;
    timestamp: string;
  };
}

// Default system message variables
export const DEFAULT_MESSAGE_VARIABLES: MessageVariable[] = [
  {
    id: 'contact_first_name',
    name: 'Contact First Name',
    variable: ':contact_first_name:',
    description: 'Auto-populates the recipient\'s first name',
    category: 'contact',
    isGlobal: true,
    isActive: true,
    dataSource: 'contact',
    dataField: 'first_name'
  },
  {
    id: 'contact_full_name',
    name: 'Contact Full Name',
    variable: ':contact_full_name:',
    description: 'Auto-populates the recipient\'s full name',
    category: 'contact',
    isGlobal: true,
    isActive: true,
    dataSource: 'contact',
    dataField: 'full_name'
  },
  {
    id: 'business_name',
    name: 'Business Name',
    variable: ':business_name:',
    description: 'Auto-populates the name of the business or specific location.',
    category: 'business',
    isGlobal: true,
    isActive: true,
    dataSource: 'business',
    dataField: 'name'
  },
  {
    id: 'business_contact_first_name',
    name: 'Business Contact First Name',
    variable: ':business_contact_first_name:',
    description: 'Inserts the first name of the person sending the message, making it more personal.',
    category: 'business',
    isGlobal: true,
    isActive: true,
    dataSource: 'business',
    dataField: 'contact_first_name'
  },
  {
    id: 'business_contact_full_name',
    name: 'Business Contact Full Name',
    variable: ':business_contact_full_name:',
    description: 'Inserts the full name of the business contact, providing a more formal tone.',
    category: 'business',
    isGlobal: true,
    isActive: true,
    dataSource: 'business',
    dataField: 'contact_full_name'
  },
  {
    id: 'business_office_number',
    name: 'Business Office Number',
    variable: ':business_office_number:',
    description: 'Inserts the business\'s office phone number from their profile.',
    category: 'business',
    isGlobal: true,
    isActive: true,
    dataSource: 'business',
    dataField: 'office_number'
  },
  {
    id: 'new_post_highlight',
    name: 'Business New Post Highlight',
    variable: ':new_post_highlight:',
    description: 'Generates a one-time link to the business\'s most recent post for customer engagement.',
    category: 'links',
    isGlobal: true,
    isActive: true,
    dataSource: 'api',
    dataField: 'new_post_highlight'
  },
  {
    id: 'create_post_invite',
    name: 'Invite User to Post',
    variable: ':create_post_invite:',
    description: 'Creates a one-time link for the recipient to make a new post, guiding them through the signup process if needed.',
    category: 'links',
    isGlobal: true,
    isActive: true,
    dataSource: 'api',
    dataField: 'create_post_invite'
  },
  {
    id: 'review_link',
    name: 'Review Link',
    variable: ':review_link:',
    description: 'Generates a one-time link that directs the customer to the business\'s review page.',
    category: 'links',
    isGlobal: true,
    isActive: true,
    dataSource: 'api',
    dataField: 'review_link'
  },
  {
    id: 'friend_invite_link',
    name: 'Friend Invite Link',
    variable: ':friend_invite_link:',
    description: 'Generates a one-time referral link allowing businesses to invite their peers to join Everybrand.',
    category: 'links',
    isGlobal: true,
    isActive: true,
    dataSource: 'api',
    dataField: 'friend_invite_link'
  }
];

// Message variable processing utilities
export class MessageVariableProcessor {
  private variables: MessageVariable[];
  private context: MessageVariableContext;

  constructor(variables: MessageVariable[], context: MessageVariableContext) {
    this.variables = variables;
    this.context = context;
  }

  /**
   * Process a message template and replace all variables with actual values
   */
  processMessage(template: string): string {
    let processedMessage = template;

    // Process each variable
    this.variables.forEach(variable => {
      if (!variable.isActive) return;

      const value = this.getVariableValue(variable);
      if (value !== null) {
        // Replace all instances of the variable
        const regex = new RegExp(variable.variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        processedMessage = processedMessage.replace(regex, value);
      }
    });

    return processedMessage;
  }

  /**
   * Get the value for a specific variable
   */
  private getVariableValue(variable: MessageVariable): string | null {
    switch (variable.dataSource) {
      case 'contact':
        return this.getContactValue(variable.dataField);
      case 'business':
        return this.getBusinessValue(variable.dataField);
      case 'system':
        return this.getSystemValue(variable.dataField);
      case 'api':
        return this.getApiValue(variable);
      default:
        return null;
    }
  }

  /**
   * Get contact-related values
   */
  private getContactValue(field?: string): string | null {
    if (!this.context.contact) return null;

    switch (field) {
      case 'first_name':
        return this.context.contact.first_name;
      case 'last_name':
        return this.context.contact.last_name;
      case 'full_name':
        return this.context.contact.full_name || 
               `${this.context.contact.first_name} ${this.context.contact.last_name}`;
      case 'phone_number':
        return this.context.contact.phone_number;
      case 'email':
        return this.context.contact.email || '';
      default:
        return null;
    }
  }

  /**
   * Get business-related values
   */
  private getBusinessValue(field?: string): string | null {
    if (!this.context.business) return null;

    switch (field) {
      case 'name':
        return this.context.business.name;
      case 'contact_first_name':
        return this.context.business.contact_first_name;
      case 'contact_last_name':
        return this.context.business.contact_last_name;
      case 'contact_full_name':
        return this.context.business.contact_full_name || 
               `${this.context.business.contact_first_name} ${this.context.business.contact_last_name}`;
      case 'office_number':
        return this.context.business.office_number;
      case 'email':
        return this.context.business.email || '';
      default:
        return null;
    }
  }

  /**
   * Get system-related values
   */
  private getSystemValue(field?: string): string | null {
    if (!this.context.system) return null;

    switch (field) {
      case 'current_user_id':
        return this.context.system.current_user_id;
      case 'current_business_id':
        return this.context.system.current_business_id;
      case 'timestamp':
        return this.context.system.timestamp;
      default:
        return null;
    }
  }

  /**
   * Get API-generated values (links, etc.)
   */
  private getApiValue(variable: MessageVariable): string | null {
    // This would typically make API calls to generate dynamic links
    // For now, return placeholder values
    switch (variable.id) {
      case 'new_post_highlight':
        return '[Link to latest post]';
      case 'create_post_invite':
        return '[Link to create post]';
      case 'review_link':
        return '[Link to reviews]';
      case 'friend_invite_link':
        return '[Link to invite friends]';
      default:
        return null;
    }
  }

  /**
   * Get all available variables for a specific category
   */
  getVariablesByCategory(category: MessageVariable['category']): MessageVariable[] {
    return this.variables.filter(variable => 
      variable.category === category && variable.isActive
    );
  }

  /**
   * Get all global variables
   */
  getGlobalVariables(): MessageVariable[] {
    return this.variables.filter(variable => variable.isGlobal && variable.isActive);
  }

  /**
   * Get all custom variables for a specific user
   */
  getCustomVariables(userId: string): MessageVariable[] {
    return this.variables.filter(variable => 
      !variable.isGlobal && variable.createdBy === userId && variable.isActive
    );
  }

  /**
   * Validate a message template for unknown variables
   */
  validateTemplate(template: string): { isValid: boolean; unknownVariables: string[] } {
    const unknownVariables: string[] = [];
    const variableRegex = /:([a-zA-Z_][a-zA-Z0-9_]*):/g;
    let match;

    while ((match = variableRegex.exec(template)) !== null) {
      const variable = `:${match[1]}:`;
      const exists = this.variables.some(v => v.variable === variable);
      if (!exists) {
        unknownVariables.push(variable);
      }
    }

    return {
      isValid: unknownVariables.length === 0,
      unknownVariables
    };
  }
}

// Utility functions
export const createMessageVariable = (
  name: string,
  variable: string,
  description: string,
  category: MessageVariable['category'],
  dataSource: MessageVariable['dataSource'],
  dataField?: string,
  createdBy?: string
): MessageVariable => {
  return {
    id: variable.replace(/[:]/g, ''),
    name,
    variable,
    description,
    category,
    isGlobal: false,
    isActive: true,
    dataSource,
    dataField,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy
  };
};

export const formatMessageVariable = (variable: string): string => {
  return variable.startsWith(':') && variable.endsWith(':') 
    ? variable 
    : `:${variable.replace(/[:]/g, '')}:`;
};

export const parseMessageVariables = (text: string): string[] => {
  const variableRegex = /:([a-zA-Z_][a-zA-Z0-9_]*):/g;
  const variables: string[] = [];
  let match;

  while ((match = variableRegex.exec(text)) !== null) {
    variables.push(`:${match[1]}:`);
  }

  return [...new Set(variables)]; // Remove duplicates
};

